import { Release, ReleaseTarget } from '@/types/backend';
import { isReleaseArray } from '@/util/typeGuards';

export const release = (baseUrl: string, fetchWithAuth: (url: string, options?: RequestInit) => Promise<Response>) => {
  return {
    /**
     * Get a release by Release GUID
     * @param releaseId Release GUID
     * @returns
     */
    get: async (releaseId: string): Promise<Release> => {
      const url = new URL(`${baseUrl}/Get`);
      const params = new URLSearchParams();
      params.append('releaseId', releaseId);
      url.search = params.toString();
      const response = await fetchWithAuth(url.toString());
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      console.debug('release.get response', data);
      return data;
    },

    /**
     * Get all current releases for a given Release Target. Optionally filter via Application GUID.
     * @param target Release target, All=1 or Whitelist=2
     * @param applicationId Optional Application GUID
     * @returns
     */
    current: async (target: ReleaseTarget, applicationId: string): Promise<Release[]> => {
      const url = new URL(`${baseUrl}/Current`);
      const params = new URLSearchParams();
      params.append('target', target.toString());
      params.append('applicationId', applicationId);
      url.search = params.toString();
      const response = await fetchWithAuth(url.toString());
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      console.debug('release.current response', data);
      if (isReleaseArray(data)) {
        data.sort((a, b) => {
          if (a.revision === undefined) return 1;
          if (b.revision === undefined) return -1;
          return a.revision - b.revision;
        });
      }
      return data;
    },

    /**
     * Get release history for a given Application.
     * @param applicationId Application GUID
     * @returns
     */
    history: async (applicationId: string): Promise<Release[]> => {
      const url = new URL(`${baseUrl}/History`);
      const params = new URLSearchParams();
      params.append('applicationId', applicationId);
      url.search = params.toString();
      const response = await fetchWithAuth(url.toString());
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      console.debug('release.history response', data);
      if (isReleaseArray(data) && data.every((r) => r.created)) {
        data.sort((a, b) => {
          if (a.created === undefined) return 1;
          if (b.created === undefined) return -1;
          return a.created.localeCompare(b.created);
        });
      }
      return data;
    },

    /**
     * Create a new release with given Application and Revision for given Release Target.
     * @param applicationId Application GUID
     * @param revision Application revision to release
     * @param releasetarget Release target, All=1 or Whitelist=2
     * @returns
     */
    create: async (applicationId: string, revision: number, releasetarget: ReleaseTarget): Promise<string> => {
      const url = `${baseUrl}/Create`;
      const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ applicationId, revision, releasetarget })
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const result = await response.text();
      console.debug('release.create response', result);
      return result;
    },

    /**
     * Revert an existing active release to it's previously released version if one exists, otherwise inactivated application.
     * @param releaseId Release GUID to revert
     * @returns
     */
    revert: async (releaseId: string) => {
      const url = `${baseUrl}/Revert`;
      const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ releaseId })
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const result = await response.json();
      console.debug('release.revert response', result);
      return result;
    }
  };
};
